import { useEffect, useRef, useState } from "react";
import RemoteStorage from "remotestoragejs";
import type { RSModule } from "remotestoragejs";
import { Todonna } from "@/lib/remotestorage-todonna";
import { Logs } from "@/lib/remotestorage-logs";

// Singleton instance
let remoteStorageInstance: RemoteStorage | null = null;
let isInitialized = false;
let loadedModuleNames = new Set<string>(['todonna', 'logs']); // Track loaded modules

interface UseRemoteStorageOptions {
  modules?: RSModule[];
  accessClaims?: Record<string, 'r' | 'rw'>;
  apiKeys?: {
    googledrive?: string;
    dropbox?: string;
  };
}

/**
 * Hook to access the singleton RemoteStorage instance.
 * Ensures only one instance is created and access claims are done immediately after creation.
 *
 * @param options - Configuration options
 * @param options.modules - Array of RemoteStorage modules to load
 * @param options.accessClaims - Object defining access scopes (e.g., { 'ai-wallet': 'rw' })
 * @param options.apiKeys - API keys for storage providers
 * @returns The RemoteStorage instance
 *
 * @example
 * import { AI } from "remotestorage-module-ai-wallet";
 * const remoteStorage = useRemoteStorage({
 *   modules: [AI],
 *   accessClaims: { 'ai-wallet': 'rw', 'todonna': 'rw' }
 * });
 */
export function useRemoteStorage(options?: UseRemoteStorageOptions): RemoteStorage | null {
  const hasClaimedAccess = useRef(false);
  const [instance, setInstance] = useState<RemoteStorage | null>(remoteStorageInstance);
  useEffect(() => {
    // Create singleton instance if it doesn't exist
    if (!remoteStorageInstance) {
      // Always include Todonna and Logs modules by default
      const modules = [Todonna, Logs, ...(options?.modules || [])];

      // Track which modules are being loaded
      loadedModuleNames = new Set(['todonna', 'logs']);
      modules.forEach(module => {
        if (module.name) {
          loadedModuleNames.add(module.name);
        }
      });
      remoteStorageInstance = new RemoteStorage({
        modules
      });
      isInitialized = false;
      setInstance(remoteStorageInstance);
    }

    // Configure API keys if provided (only set non-empty keys)
    if (options?.apiKeys && remoteStorageInstance && !isInitialized) {
      const validApiKeys: Record<string, string> = {};
      if (options.apiKeys.googledrive && options.apiKeys.googledrive.trim()) {
        validApiKeys.googledrive = options.apiKeys.googledrive;
      }
      if (options.apiKeys.dropbox && options.apiKeys.dropbox.trim()) {
        validApiKeys.dropbox = options.apiKeys.dropbox;
      }

      // Only call setApiKeys if we have at least one valid key
      if (Object.keys(validApiKeys).length > 0) {
        try {
          remoteStorageInstance.setApiKeys(validApiKeys);
        } catch (error) {
          console.error('Failed to set API keys:', error);
        }
      }
    }

    // Default access claims - merge with user-provided claims
    const defaultClaims: Record<string, 'r' | 'rw'> = {
      'todonna': 'rw' as const,
      'logs': 'rw' as const,
      ...(options?.accessClaims || {})
    };

    // Only claim access to modules that are actually loaded
    const claimsToMake: Record<string, 'r' | 'rw'> = {};
    Object.entries(defaultClaims).forEach(([module_0, mode]) => {
      if (loadedModuleNames.has(module_0)) {
        claimsToMake[module_0] = mode;
      }
    });

    // Claim access immediately after instance creation (only once)
    if (!hasClaimedAccess.current && !isInitialized && remoteStorageInstance) {
      // Mark as claimed immediately to prevent multiple setTimeout calls
      hasClaimedAccess.current = true;

      // Wait a tick to ensure RemoteStorage is fully initialized
      // This helps avoid race conditions with backendManager initialization
      setTimeout(() => {
        if (!remoteStorageInstance) {
          hasClaimedAccess.current = false;
          isInitialized = false;
          return;
        }
        try {
          // Check if access and caching are available
          if (!remoteStorageInstance.access || !remoteStorageInstance.caching) {
            console.warn('RemoteStorage access or caching not available yet');
            hasClaimedAccess.current = false;
            isInitialized = false;
            return;
          }
          Object.entries(claimsToMake).forEach(([module_1, mode_0]) => {
            try {
              remoteStorageInstance!.access.claim(module_1, mode_0);
            } catch (error_1) {
              console.error(`Failed to claim access for module ${module_1}:`, error_1);
            }
          });

          // Enable caching only for modules we've claimed access to
          Object.keys(claimsToMake).forEach(module_2 => {
            try {
              remoteStorageInstance!.caching.enable(`/${module_2}/`);
            } catch (error_2) {
              console.error(`Failed to enable caching for module ${module_2}:`, error_2);
            }
          });
          isInitialized = true;
        } catch (error_0) {
          console.error('Failed to claim access or enable caching:', error_0);
          hasClaimedAccess.current = false;
          isInitialized = false;
        }
      }, 0);
    }
  }, [options?.accessClaims, options?.modules, options?.apiKeys]);
  return instance;
}